import React, { useState, useEffect } from 'react';
import { getAPI } from '../utils/config';
import axios from 'axios';
import { toast } from 'sonner';

const AnalysisLimitModal = ({ isOpen, onClose, user }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPackages();
      // Start animation
      setTimeout(() => setIsAnimating(true), 100);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${getAPI()}/credits/packages`);
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to load packages:', error);
    }
  };

  const handlePurchase = async (pkg) => {
    if (!user || !user.id) {
      toast.error('Please log in to purchase credits');
      return;
    }

    setIsLoading(true);
    setSelectedPackage(pkg);

    try {
      const response = await axios.post(`${getAPI()}/credits/purchase/stripe-checkout`, {
        user_id: user.id,
        package_id: pkg.id
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create checkout';
      toast.error(message);
      console.error('Checkout error:', error);
    } finally {
      setIsLoading(false);
      setSelectedPackage(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[9999]"
      onClick={onClose}
    >
      <div 
        className={`bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl transform transition-all duration-500 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Animation */}
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 p-8 text-center">
          {/* Animated background particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full opacity-20 animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${3 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>

          {/* Icon with bounce animation */}
          <div className="relative mb-4">
            <div className="inline-block animate-bounce">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-2 relative z-10">
            🎉 You've Used Your Free Analyses!
          </h2>
          <p className="text-white/90 text-lg relative z-10">
            Unlock unlimited AI magic with credits
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Credit Packages */}
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {packages.length > 0 ? (
              packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handlePurchase(pkg)}
                  disabled={isLoading && selectedPackage?.id === pkg.id}
                  className="w-full bg-white hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 rounded-xl p-4 border-2 border-gray-200 hover:border-purple-400 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                          {pkg.credits}
                        </span>
                        <span className="text-gray-600 font-medium">Credits</span>
                        {pkg.bonus_credits > 0 && (
                          <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                            +{pkg.bonus_credits} BONUS
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {pkg.videos || 0} videos
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {isLoading && selectedPackage?.id === pkg.id ? (
                        <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-gray-900">
                            ${typeof pkg.price === 'number' ? pkg.price.toFixed(2) : '0.00'}
                          </div>
                          <div className="text-xs text-purple-600 font-medium">
                            Buy Now →
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading packages...</p>
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full mt-6 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisLimitModal;
